'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { getMarkerIcon } from './markerIcons';

/**
 * Display labels for entity types (singular form for autofill dropdown)
 */
const TYPE_LABELS = {
  heart: 'Heart',
  artist: 'Artist',
  attraction: 'Attraction',
  event: 'Event',
  community: 'Community',
  promotion: 'Promotion'
};

/**
 * Highlight matching text in the title
 * @param {string} text - Full text to display
 * @param {string} query - Search query to highlight
 * @returns {JSX.Element} Text with highlighted matching portion
 */
function HighlightedText(t0) {
  const $ = _c(20);
  const {
    text,
    query
  } = t0;
  if (!query.trim()) {
    let t1;
    if ($[0] !== text) {
      t1 = <span className="text-[#323232] lg:text-base text-[15px] font-normal leading-[1em]">{text}</span>;
      $[0] = text;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let beforeMatch;
  let matchIndex;
  let t1;
  let t2;
  if ($[2] !== query || $[3] !== text) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const lowerText = text.toLowerCase();
      const lowerQuery = query.toLowerCase();
      matchIndex = lowerText.indexOf(lowerQuery);
      if (matchIndex === -1) {
        let t3;
        if ($[8] !== text) {
          t3 = <span className="text-[#323232] lg:text-base text-[15px] font-normal leading-[1em]">{text}</span>;
          $[8] = text;
          $[9] = t3;
        } else {
          t3 = $[9];
        }
        t2 = t3;
        break bb0;
      }
      beforeMatch = text.slice(0, matchIndex);
      t1 = text.slice(matchIndex, matchIndex + query.length);
    }
    $[2] = query;
    $[3] = text;
    $[4] = beforeMatch;
    $[5] = matchIndex;
    $[6] = t1;
    $[7] = t2;
  } else {
    beforeMatch = $[4];
    matchIndex = $[5];
    t1 = $[6];
    t2 = $[7];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  const match = t1;
  let t3;
  if ($[10] !== matchIndex || $[11] !== query.length || $[12] !== text) {
    t3 = text.slice(matchIndex + query.length);
    $[10] = matchIndex;
    $[11] = query.length;
    $[12] = text;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const afterMatch = t3;
  let t4;
  if ($[14] !== match) {
    t4 = <span className="text-[#323232] font-semibold">{match}</span>;
    $[14] = match;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== afterMatch || $[17] !== beforeMatch || $[18] !== t4) {
    t5 = <span className="font-normal text-[#323232] lg:text-base text-[15px] leading-[1em]">{beforeMatch}{t4}{afterMatch}</span>;
    $[16] = afterMatch;
    $[17] = beforeMatch;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
}

/**
 * Single suggestion row in the autofill dropdown
 */
function SuggestionRow(t0) {
  const $ = _c(21);
  const {
    item,
    query,
    onSelect
  } = t0;
  let t1;
  if ($[0] !== item.type) {
    t1 = getMarkerIcon(item.type);
    $[0] = item.type;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const icon = t1;
  const typeLabel = TYPE_LABELS[item.type] || "Item";
  let t2;
  if ($[2] !== item || $[3] !== onSelect) {
    t2 = () => onSelect(item);
    $[2] = item;
    $[3] = onSelect;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== item.name || $[6] !== query) {
    t3 = <div className="flex-1 text-left pr-4 flex"><HighlightedText text={item.name} query={query} /></div>;
    $[5] = item.name;
    $[6] = query;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== typeLabel) {
    t4 = <span className="lg:text-sm text-[10px] text-[#626262]">{typeLabel}</span>;
    $[8] = typeLabel;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = `w-4 h-4 grayscale  ${typeLabel == "Promotion" ? "opacity-60" : "opacity-50"}`;
  let t6;
  if ($[10] !== icon.alt || $[11] !== icon.src || $[12] !== t5) {
    t6 = <img src={icon.src} alt={icon.alt} className={t5} />;
    $[10] = icon.alt;
    $[11] = icon.src;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t4 || $[15] !== t6) {
    t7 = <div className="flex items-center gap-2 flex-shrink-0">{t4}{t6}</div>;
    $[14] = t4;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t2 || $[18] !== t3 || $[19] !== t7) {
    t8 = <button type="button" onClick={t2} className="w-full flex items-start justify-between px-4 py-3 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none border-b border-[#D9D9D9] last:border-b-0 cursor-pointer">{t3}{t7}</button>;
    $[17] = t2;
    $[18] = t3;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}

/**
 * Search autofill dropdown component
 * Shows suggestions as user types, with entity type labels and icons
 *
 * @param {Object} props
 * @param {string} props.query - Current search query
 * @param {Array} props.items - All available items to search through
 * @param {Object} props.filters - Current active filters
 * @param {Function} props.filterMarkers - Function to apply filters to items
 * @param {Function} props.onSelect - Callback when user selects a suggestion
 * @param {boolean} props.isOpen - Whether the dropdown is visible
 */
export function SearchAutofill(t0) {
  const $ = _c(16);
  const {
    query,
    items,
    filters,
    filterMarkers,
    onSelect,
    isOpen
  } = t0;
  let t1;
  bb0: {
    if (!query.trim() || !items?.length) {
      let t2;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [];
        $[0] = t2;
      } else {
        t2 = $[0];
      }
      t1 = t2;
      break bb0;
    }
    let t2;
    if ($[1] !== filterMarkers || $[2] !== filters || $[3] !== items || $[4] !== query) {
      const searchTerm = query.toLowerCase();
      const matchingItems = items.filter(item => item.name?.toLowerCase().includes(searchTerm));
      const filteredItems = filterMarkers ? filterMarkers(matchingItems, filters, "list", true) : matchingItems;
      t2 = filteredItems.slice(0, 10);
      $[1] = filterMarkers;
      $[2] = filters;
      $[3] = items;
      $[4] = query;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    t1 = t2;
  }
  const suggestions = t1;
  if (!isOpen) {
    return null;
  }
  if (query.trim() && suggestions.length === 0) {
    let t2;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-b-lg shadow-lg z-50 overflow-hidden"><div className="px-4 py-3 text-[#323232] lg:text-base text-[15px]">No results found.</div></div>;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    return t2;
  }
  if (!query.trim()) {
    return null;
  }
  let t2;
  if ($[7] !== onSelect || $[8] !== query || $[9] !== suggestions) {
    let t3;
    if ($[11] !== onSelect || $[12] !== query) {
      t3 = item_0 => <SuggestionRow key={`${item_0.type}-${item_0.id}`} item={item_0} query={query} onSelect={onSelect} />;
      $[11] = onSelect;
      $[12] = query;
      $[13] = t3;
    } else {
      t3 = $[13];
    }
    t2 = suggestions.map(t3);
    $[7] = onSelect;
    $[8] = query;
    $[9] = suggestions;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[14] !== t2) {
    t3 = <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-b-lg shadow-lg z-50 overflow-hidden max-h-80 overflow-y-auto">{t2}</div>;
    $[14] = t2;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  return t3;
}